import { channels, configureChannels } from "./channels"
import type { TPageView } from "./channels/contract"
import { maskTenantName, templateEndpoint } from "./params"

export {
	foldToGAParams,
	gaEventName,
	humaniseRouteTemplate,
	maskTenantName,
	sanitiseEventParams,
	templateEndpoint,
} from "./params"
export type { TPageView } from "./channels/contract"

export function bootstrap(config: Parameters<typeof configureChannels>[0]) {
	configureChannels(config)
}

export type TrackingShareMethod = "email" | "whatsapp" | "copy" | "native"

export type TrackingShareContentType =
	| "cab_schedules"
	| "quotes"
	| "hotel_bookings"
	| "trip_voucher"
	| "trip_hotels_voucher"
	| "trip_holding_email"
	| "error_report"
	| "trip"

export type TrackingShareContentId =
	| "booking_enquiry"
	| "driver"
	| "guest"
	| "transport_service_provider"
	| "sembark_support"
	| "trip_details"

export type TrackingWhatsAppMessageKind =
	| "text"
	| "attachment"
	| "template"
	| "quick_send"

/** Where in the app a WhatsApp message was sent from. */
export type TrackingWhatsAppSurface =
	| "inbox"
	| "trip"
	| "trip_plan_request"
	| "integration"

type TDurationBucket = "<2s" | "2-5s" | "5-10s" | "10s+"

/**
 * Every event the app sends, with its parameters. String parameters are
 * unions on purpose: GA4 turns an unbounded parameter into an "(other)"
 * row, so a value that could be anything must not compile.
 */
export type TTrackingEventParams = {
	login: { in_app: boolean }
	logout: undefined
	sign_up: { method: "user" | "tenant" }
	share: {
		method: TrackingShareMethod
		content_type: TrackingShareContentType
		content_id: TrackingShareContentId
	}
	whatsapp_message_sent: {
		kind: TrackingWhatsAppMessageKind
		surface: TrackingWhatsAppSurface
	}
	web_app_update_available: undefined
	web_app_update_clicked: undefined
	web_app_opened_via_pwa_install: undefined
	web_app_opened_via_ms_install: undefined
	/**
	 * A response that took 10 seconds or more on the server. Kept only for
	 * continuity with the old `api_response_time` event; it will be removed
	 * in a future update, so do not register its parameters in GA.
	 */
	slow_api_response: {
		method: string
		endpoint: string
		bucket: TDurationBucket
		backend_bucket: TDurationBucket
		duration_ms: number
	}
	incorrect_timezone: {
		user_timezone_offset: number
		tenant_timezone_offset: number
	}
	hotkey: { label: string }
	release_notes_opened: undefined
	release_notes_read: undefined
}

export type TTrackingEvent = keyof TTrackingEventParams

export const TrackingEvents = {
	login: "login",
	logout: "logout",
	sign_up: "sign_up",
	share: "share",
	whatsapp_message_sent: "whatsapp_message_sent",
	web_app_update_available: "web_app_update_available",
	web_app_update_clicked: "web_app_update_clicked",
	web_app_opened_via_pwa_install: "web_app_opened_via_pwa_install",
	web_app_opened_via_ms_install: "web_app_opened_via_ms_install",
	slow_api_response: "slow_api_response",
	incorrect_timezone: "incorrect_timezone",
	hotkey: "hotkey",
	release_notes_opened: "release_notes_opened",
	release_notes_read: "release_notes_read",
} as const satisfies { [K in TTrackingEvent]: K }

type TEventArgs<E extends TTrackingEvent> =
	TTrackingEventParams[E] extends undefined
		? []
		: [data: TTrackingEventParams[E]]

export function trackEvent<E extends TTrackingEvent>(
	event: E,
	...args: TEventArgs<E>
): void {
	channels.track(event, args[0])
}

/**
 * A page view for the route the app just navigated to. `path` must be the
 * route template, never the real URL.
 */
export function trackPageView(view: TPageView): void {
	channels.page(view)
}

/**
 * =============================================================
 * ==========
 * ========== Here are commonly used tracking events ===========
 * ==========
 * =============================================================
 * These are simply high-order proxy functions to track different
 * events.
 */

function durationBucket(seconds: number): TDurationBucket {
	return seconds >= 10
		? "10s+"
		: seconds >= 5
			? "5-10s"
			: seconds >= 2
				? "2-5s"
				: "<2s"
}

/**
 * All values are in seconds. Only responses the server spent 10 seconds or
 * more on are reported.
 *
 * @deprecated Slated for removal in a future update along with the
 * `slow_api_response` event.
 */
export function trackApiResponseTime({
	totalTime,
	url,
	method,
	backendServerTime,
}: {
	totalTime: number | string
	url: string
	method: string
	backendServerTime: string
}): void {
	const backend_bucket = durationBucket(Number(backendServerTime))
	if (backend_bucket === "10s+") {
		trackEvent(TrackingEvents.slow_api_response, {
			method,
			endpoint: templateEndpoint(url),
			bucket: durationBucket(Number(totalTime)),
			backend_bucket,
			duration_ms: Math.round(Number(totalTime) * 1000),
		})
	}
}

export function trackLogin(type?: "re_authenticate") {
	trackEvent(TrackingEvents.login, { in_app: type === "re_authenticate" })
}

export function trackSignup(type: "user" | "tenant") {
	trackEvent(TrackingEvents.sign_up, { method: type })
}

export function trackLogout() {
	trackEvent(TrackingEvents.logout)
	setAnanlyticsIdentity(null)
}

export function trackShare(data: TTrackingEventParams["share"]) {
	trackEvent(TrackingEvents.share, data)
}

export function trackWhatsAppMessageSent(
	data: TTrackingEventParams["whatsapp_message_sent"]
) {
	trackEvent(TrackingEvents.whatsapp_message_sent, data)
}

let USER_ID: string | undefined

export function setUserProperties(properties: {
	tenant_name?: string
	user_id: string
}) {
	channels.identify(properties.user_id, {
		tenantName: properties.tenant_name
			? maskTenantName(properties.tenant_name)
			: undefined,
	})
}

export function setAnanlyticsIdentity(
	user: { uid: string; tenant?: { name?: string } } | null | undefined
) {
	if (user && USER_ID === user.uid) return
	USER_ID = user?.uid
	if (user) {
		setUserProperties({
			tenant_name: user.tenant?.name || undefined,
			user_id: user.uid,
		})
	} else {
		channels.resetIdentity()
	}
}

export function trackHotkey(hotkey: string) {
	trackEvent(TrackingEvents.hotkey, {
		label: hotkey,
	})
}
